import { formatDate, Button, Anchor, PageHeader, SearchBar, Filters, DropdownSelect, colourString, SkillLevel, SkillPercentage, Pager, Modal } from '@keplerco/core';
import classNames from 'classnames';
import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { CompletionStatusChip } from '../../components/chips/completion-status.chip';
import { EmptyState } from '../../components/general/empty-state/empty-state';
import { SkeletonOverlay } from '../../components/general/loading-state/skeleton-overlay';
import { PagerConnector } from '../../components/general/pager-connector/pager-connector';
import { SortBy } from '../../components/general/sort-by/sort-by';
import { CompletionStatus, FetchType, OrganizationLevelType } from '../../enums';
import { SortByOption } from '../../enums/sort-by-option';
import { ASSESSMENT_SUB_TYPE_OPTIONS, COMPLETION_STATUS_OPTIONS } from '../../library/consts/consts';
import { getSubdomain } from '../../library/helpers/get-subdomain';
import { useKeplerNavigate } from '../../navigation/guards/use-kepler-navigate';
import { PagePath } from '../../navigation/navigation.enums';
import { NotificationType, NotificationPriority } from '../../notifications/notifications.models';
import { useAppActions, useAppState } from '../../overmind';
import { ConfirmationModalLayout } from '../../widgets/layouts';
import { getBorderColorByCompletionStatus, yourAssessmentSortByOptions } from './your-assessments.helpers';
import styles from './your-assessments.module.css';
import { LearnerAssessmentsSearchParams, LearnerAssessmentResponse } from '../../overmind/actions';
import { getAssessmentSubTypeDescription } from '../../library/helpers/get-assessment-sub-type-description';

function generateDates(assessment: LearnerAssessmentResponse) {
  const dates = [];

  if (!!assessment.dateCreated) {
    dates.push(`Assigned: ${formatDate(new Date(assessment.dateCreated))}`);
  }
  if (!!assessment.dateCompleted && assessment.completionStatus !== CompletionStatus.InProgress) {
    dates.push(`Completed: ${formatDate(new Date(assessment.dateCompleted))}`);
  } else if (!!assessment.endDate) {
    dates.push(`Deadline: ${formatDate(new Date(assessment.endDate))}`);
  }

  return dates.length > 0 ? dates : undefined;
}

function renderDates(assessment: LearnerAssessmentResponse): React.ReactElement {
  const dates = generateDates(assessment);
  if (!dates) return <React.Fragment />;

  return (
    <div className={styles.datesContainer}>
      {dates.map(date => (
        <div className={classNames('caption', styles.dateText)} key={date}>
          {date}
        </div>
      ))}
    </div>
  );
}

export function YourAssessmentsPage() {
  const subdomain = getSubdomain();
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables, user } = useAppState();

  const defaultSearchParams = useMemo<LearnerAssessmentsSearchParams>(
    () => ({
      organizationLevel: OrganizationLevelType.Learner,
      companySlug: companyVariables.slug,
      departmentSlug: undefined,
      teamSlug: undefined,
      learnerSlug: user?.learnerSlug,
      search: undefined,
      sortBy: SortByOption.MostRecentAssigned,
      pageSize: 10,
      page: 1,
      isArchived: false,
    }),
    [companyVariables.slug, user?.learnerSlug]
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [request, setRequest] = useState<LearnerAssessmentsSearchParams>(defaultSearchParams);
  const [assessments, setAssessments] = useState<LearnerAssessmentResponse[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [reassessment, setReassessment] = useState<LearnerAssessmentResponse | undefined>(undefined);

  async function fetchAssessments(params: LearnerAssessmentsSearchParams) {
    const response = await actions.getLearnerAssessments(params);
    if (!response) return;

    setAssessments(response.items);
    setTotalPages(response.totalPages);
    setIsLoading(false);
    setIsSearching(false);
    return response;
  }

  useEffect(() => {
    async function initialise() {
      setIsLoading(true);
      await fetchAssessments(request);
    }

    initialise();
  }, []);

  async function handleSearch(event: FormEvent<HTMLInputElement>) {
    setIsSearching(true);
    const searchValue = (event.target as HTMLInputElement).value.trim() || undefined;

    const updatedRequest = {
      ...request,
      search: searchValue,
      page: 1,
    };

    setRequest(updatedRequest);
    await fetchAssessments(updatedRequest);

    setIsSearching(false);
  }

  async function clearFilters() {
    setIsLoading(true);
    setRequest(defaultSearchParams);
    await fetchAssessments(defaultSearchParams);
  }

  async function applyFilters() {
    setIsLoading(true);
    setRequest({ ...request, page: 1 });
    await fetchAssessments({ ...request, page: 1 });
  }

  async function handlePageChange(page: number) {
    setIsLoading(true);
    setRequest({ ...request, page });
    await fetchAssessments({ ...request, page });
  }

  function renderButton(assessment: LearnerAssessmentResponse): React.ReactElement {
    switch (assessment.completionStatus) {
      case CompletionStatus.NotStarted:
        return (
          <Button
            onClick={async () => {
              const result = await actions.getAssessmentConfig(assessment.assessmentSlug);
              if (!!result) keplerNavigate(`${PagePath.analysisBase}${PagePath.analysisAssessment.replace(':assessmentSlug', assessment.assessmentSlug)}`);
            }}
            type="button"
          >
            Start
          </Button>
        );
      case CompletionStatus.InProgress:
        return (
          <Button
            onClick={async () => {
              const result = await actions.getAssessmentConfig(assessment.assessmentSlug);
              if (!!result) keplerNavigate(`${PagePath.analysisBase}${PagePath.analysisAssessment.replace(':assessmentSlug', assessment.assessmentSlug)}`);
            }}
            type={'button'}
          >
            Continue
          </Button>
        );
      case CompletionStatus.Completed:
        return assessment.allowReassessment ? (
          <React.Fragment>
            <Anchor
              onClick={e => {
                e.stopPropagation();
                setReassessment(assessment);
              }}
            >
              Retake
            </Anchor>
            <Button onClick={() => keplerNavigate(`${PagePath.yourAssessmentsBase}${PagePath.yourAssessmentsAssessment.replace(':assessmentSlug', assessment.assessmentSlug)}`)} type="button">
              View Results
            </Button>
          </React.Fragment>
        ) : (
          <Button onClick={() => keplerNavigate(`${PagePath.yourAssessmentsBase}${PagePath.yourAssessmentsAssessment.replace(':assessmentSlug', assessment.assessmentSlug)}`)} type="button">
            View Results
          </Button>
        );
      case CompletionStatus.NoActionRequired:
      default:
        return <React.Fragment />;
    }
  }

  return (
    <React.Fragment>
      <div className="pageWrapper wrapper">
        <PageHeader
          breadcrumbs={[
            {
              name: subdomain.includes('lpi') ? 'My Assessments' : 'Your Assessments',
              url: PagePath.yourAssessmentsBase,
            },
          ]}
          title={subdomain.includes('lpi') ? 'My Assessments' : 'Your Assessments'}
        />

        <div className="row" style={{ marginTop: 20, marginBottom: 20, display: 'flex', gap: 15 }}>
          <SearchBar label="Search" loading={isSearching} onInput={handleSearch} />

          <Filters onClickClear={clearFilters} onClickApply={applyFilters}>
            <DropdownSelect
              label="Type"
              value={request.assessmentType?.toString()}
              options={ASSESSMENT_SUB_TYPE_OPTIONS}
              onChange={event =>
                setRequest(current => ({
                  ...current,
                  assessmentType: event.target.value ? parseInt(event.target.value) : undefined,
                  page: 1,
                }))
              }
            />

            <DropdownSelect
              label="Status"
              options={COMPLETION_STATUS_OPTIONS}
              value={request.completionStatuses?.toString()}
              onChange={event =>
                setRequest(current => ({
                  ...current,
                  completionStatuses: !!event.target.value ? event.target.value.split(',').map(completionStatus => parseInt(completionStatus)) : undefined,
                  page: 1,
                }))
              }
            />
          </Filters>

          <SortBy
            options={yourAssessmentSortByOptions}
            value={request.sortBy}
            onChange={event => {
              setIsLoading(true);
              const nextRequest: LearnerAssessmentsSearchParams = {
                ...request,
                sortBy: event.target.value as SortByOption,
                page: 1,
              };
              setRequest(nextRequest);
              fetchAssessments(nextRequest);
            }}
          />
        </div>

        <SkeletonOverlay isLoading={isLoading} minHeight="100vh">
          {!!assessments.length ? (
            <div className={styles.pageContent}>
              {assessments.map(assessment => {
                return (
                  <div key={assessment.assessmentSlug} className={classNames('card', styles.card)} style={{ borderLeftColor: colourString(getBorderColorByCompletionStatus(assessment.completionStatus)) }}>
                    <div>
                      <div className={styles.headerContainer}>
                        <div className={styles.assessmentTypeDesktop}>{getAssessmentSubTypeDescription(assessment.assessmentSubType)}</div>

                        <div className={styles.statusContainer}>
                          <div className={styles.datesContainer}>{renderDates(assessment)}</div>
                          <CompletionStatusChip completionStatus={assessment.completionStatus} />
                        </div>
                      </div>

                      <div>
                        <h4 className={styles.assessmentName}>{assessment.assessmentName}</h4>
                      </div>

                      <div className={styles.assessmentDetailsContainer}>
                        <div className={styles.assessmentMetricsContainer}>
                          {companyVariables.useLevels ? (
                            <SkillLevel title="Your assessment score" level={assessment.score?.level ?? 0} minLevel={companyVariables.minLevel} maxLevel={companyVariables.maxLevel} noLevel={!assessment.score} />
                          ) : (
                            <SkillPercentage title="Your assessment score" percentage={assessment.score?.percentage ?? 0} noPercentage={!assessment.score} />
                          )}
                        </div>

                        <div className={styles.viewButtonContainer}>{renderButton(assessment)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {totalPages > 1 && (
                <PagerConnector pageCount={totalPages} defaultPageNumber={request.page} onPageChange={handlePageChange}>
                  {connector => <Pager {...connector} />}
                </PagerConnector>
              )}
            </div>
          ) : (
            <EmptyState />
          )}
        </SkeletonOverlay>
      </div>

      <Modal open={!!reassessment} onClose={() => setReassessment(undefined)}>
        {!!reassessment && (
          <ConfirmationModalLayout
            title="Confirm Reassessment"
            subtitle={`Are you sure you want to redo ${reassessment?.assessmentName}? If you proceed, your previous score will be overwritten with your new score...`}
            submitButtonText="Confirm"
            onClickSubmit={async () => {
              actions.startLoader({ path: PagePath.yourAssessmentsBase, type: FetchType.Sending });
              setReassessment(undefined);
              const profile = await actions.getProfile();

              if (!profile) {
                actions.addNotification({
                  active: true,
                  id: crypto.randomUUID(),
                  type: NotificationType.Error,
                  priority: NotificationPriority.Toast,
                  title: 'There was an error triggering reassessment',
                  message: 'The system was unable to retrieve your email address. Please refresh the page and try again.',
                });
                actions.stopLoader(PagePath.yourAssessmentsBase);
                return;
              }

              await actions.resetAssessment({
                assessmentSlug: reassessment.assessmentSlug,
                learnerSlug: profile.learnerSlug
              });
              await actions.getForcedAssessments();
              await actions.getAssessmentConfig(reassessment.assessmentSlug);

              actions.stopLoader(PagePath.yourAssessmentsBase);
            }}
            onClickCancel={() => setReassessment(undefined)}
          />
        )}
      </Modal>
    </React.Fragment>
  );
}
